/**
 * Dashboard Grid - Main container for draggable widgets.
 * Uses @dnd-kit for drag-and-drop functionality.
 */

import { Suspense, useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { DraggableWidget } from './DraggableWidget';
import { EditModeToolbar } from './EditModeToolbar';
import { WIDGET_REGISTRY, getVisibleWidgets } from './widgetRegistry';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import { cn } from '@/lib/utils';

export function DashboardGrid() {
  const {
    layout,
    isLoading,
    saveUserLayout,
    resetToOrgDefaults,
    hasUserCustomization,
  } = useDashboardLayout();

  const [isEditMode, setIsEditMode] = useState(false);
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const [localHidden, setLocalHidden] = useState<string[] | null>(null);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Use local state during edit, committed state otherwise
  const currentOrder = localOrder ?? layout.widgetOrder;
  const currentHidden = localHidden ?? layout.hiddenWidgets;
  const visibleWidgets = getVisibleWidgets(currentOrder, currentHidden);

  // Handle drag end - reorder widgets
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setLocalOrder(prev => {
          const order = prev ?? layout.widgetOrder;
          const oldIndex = order.indexOf(active.id as string);
          const newIndex = order.indexOf(over.id as string);
          return arrayMove(order, oldIndex, newIndex);
        });
      }
    },
    [layout.widgetOrder]
  );

  // Handle widget visibility toggle
  const handleToggleWidget = useCallback(
    (widgetId: string, visible: boolean) => {
      setLocalHidden(prev => {
        const hidden = prev ?? layout.hiddenWidgets;
        if (visible) {
          return hidden.filter(id => id !== widgetId);
        } else {
          return [...hidden, widgetId];
        }
      });

      // Also add to order if making visible and not already in order
      if (visible) {
        setLocalOrder(prev => {
          const order = prev ?? layout.widgetOrder;
          if (!order.includes(widgetId)) {
            return [...order, widgetId];
          }
          return order;
        });
      }
    },
    [layout.hiddenWidgets, layout.widgetOrder]
  );

  // Save layout changes
  const handleSave = useCallback(async () => {
    try {
      await saveUserLayout.mutateAsync({
        widgetOrder: localOrder ?? layout.widgetOrder,
        hiddenWidgets: localHidden ?? layout.hiddenWidgets,
        useOrgDefaults: false,
      });
      toast.success('Dashboard layout saved');
      setLocalOrder(null);
      setLocalHidden(null);
      setIsEditMode(false);
    } catch (error) {
      toast.error('Failed to save layout', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [localOrder, localHidden, layout, saveUserLayout]);

  // Cancel edit mode
  const handleCancel = useCallback(() => {
    setLocalOrder(null);
    setLocalHidden(null);
    setIsEditMode(false);
  }, []);

  // Reset to org defaults
  const handleResetToDefaults = useCallback(async () => {
    try {
      await resetToOrgDefaults.mutateAsync();
      toast.success('Dashboard reset to defaults');
    } catch (error) {
      toast.error('Failed to reset layout');
    }
  }, [resetToOrgDefaults]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="h-9 w-40" />
        </div>
        <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <>
      <EditModeToolbar
        isEditMode={isEditMode}
        onEnterEditMode={() => setIsEditMode(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggleWidget={handleToggleWidget}
        onResetToDefaults={handleResetToDefaults}
        hiddenWidgets={currentHidden}
        isSaving={saveUserLayout.isPending}
        hasUserCustomization={hasUserCustomization}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={currentOrder} strategy={rectSortingStrategy}>
          <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
            {visibleWidgets.map(widget => {
              const Widget = widget.component;
              const isFullWidth = widget.fullWidth;

              return (
                <DraggableWidget
                  key={widget.id}
                  id={widget.id}
                  isEditMode={isEditMode}
                  className={cn(isFullWidth && 'lg:col-span-2')}
                >
                  <Suspense fallback={<Skeleton className="h-64 w-full rounded-xl" />}>
                    <Widget />
                  </Suspense>
                </DraggableWidget>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {visibleWidgets.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No widgets visible. Click "Customize Dashboard" to add widgets.</p>
        </div>
      )}
    </>
  );
}
